import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { companyAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import {
    Key,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Copy,
    Trash2,
    Plus,
    ChevronDown,
    ChevronRight,
    Eye,
    EyeOff,
    Shield,
    Download,
    Pencil,
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';

/**
 * Inner content of the API Key management UI.
 * Reusable so it can be embedded as a tab inside CompanyManagementPage
 * without nesting a second <Layout> wrapper.
 *
 * Props:
 *  - embedded (boolean): when true, renders content only (no <Layout>),
 *    suitable for use inside another page's tab.
 */
export function APIKeyManagementContent({ embedded = false }) {
    const { user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [loading, setLoading] = useState(false);
    const [apiKeys, setApiKeys] = useState([]);
    const [availablePermissions, setAvailablePermissions] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showKeyModal, setShowKeyModal] = useState(false);
    const [newApiKey, setNewApiKey] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        permissions: [],
        ip_whitelist: '',
        rate_limit: 100
    });
    
    const [expandedSections, setExpandedSections] = useState({});
    
    // Get company_id from API
    const [companyId, setCompanyId] = useState(null);

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const API_BASE = process.env.REACT_APP_BACKEND_URL;
                const authToken = localStorage.getItem('token');
                if (!authToken) return;
                
                const res = await fetch(`${API_BASE}/api/companies/my`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.id) {
                        setCompanyId(data.id);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch company:', error);
            }
        };
        
        if (user) {
            fetchCompany();
        }
    }, [user]);

    useEffect(() => {
        if (companyId) {
            fetchApiKeys();
            fetchAvailablePermissions();
        }
    }, [companyId]);

    const fetchApiKeys = async () => {
        try {
            const response = await companyAPI.listApiKeys(companyId);
            setApiKeys(response.data || []);
        } catch (error) {
            console.error('Failed to load API keys:', error);
        }
    };

    const fetchAvailablePermissions = async () => {
        try {
            const response = await companyAPI.getAvailablePermissions(companyId);
            setAvailablePermissions(response.data?.grouped || {});
        } catch (error) {
            console.error('Failed to load permissions:', error);
        }
    };

    const handleCreateApiKey = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const ipList = formData.ip_whitelist
                ? formData.ip_whitelist.split(',').map(ip => ip.trim()).filter(ip => ip)
                : [];

            const response = await companyAPI.createApiKey(companyId, {
                name: formData.name,
                permissions: formData.permissions,
                ip_whitelist: ipList,
                rate_limit: parseInt(formData.rate_limit)
            });

            setNewApiKey(response.data);
            setShowCreateModal(false);
            setShowKeyModal(true);
            fetchApiKeys();
            
            // Reset form
            setFormData({
                name: '',
                permissions: [],
                ip_whitelist: '',
                rate_limit: 100
            });
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.detail || 'Failed to create API key'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteApiKey = async (keyId) => {
        if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
            return;
        }

        try {
            await companyAPI.deleteApiKey(companyId, keyId);
            setMessage({
                type: 'success',
                text: 'API key deleted successfully'
            });
            fetchApiKeys();
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.detail || 'Failed to delete API key'
            });
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setMessage({
            type: 'success',
            text: 'Copied to clipboard!'
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    };

    const togglePermission = (permission) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permission)
                ? prev.permissions.filter(p => p !== permission)
                : [...prev.permissions, permission]
        }));
    };

    const toggleSection = (entity) => {
        setExpandedSections(prev => ({ ...prev, [entity]: !prev[entity] }));
    };

    // ========== EDIT modal state & handlers ==========
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [editForm, setEditForm] = useState({
        name: '',
        permissions: [],
        ip_whitelist: '',
        rate_limit: 100,
    });
    const [editLoading, setEditLoading] = useState(false);
    const [editExpanded, setEditExpanded] = useState({});

    const openEditModal = (key) => {
        setEditingKey(key);
        setEditForm({
            name: key.name,
            permissions: [...(key.permissions || [])],
            ip_whitelist: (key.ip_whitelist || []).join(', '),
            rate_limit: key.rate_limit || 100,
        });
        setEditExpanded({});
        setShowEditModal(true);
    };

    const toggleEditPermission = (permission) => {
        setEditForm(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permission)
                ? prev.permissions.filter(p => p !== permission)
                : [...prev.permissions, permission]
        }));
    };

    const selectAllInEntityEdit = (entity, permissions) => {
        const allSelected = permissions.every(p => editForm.permissions.includes(p));
        if (allSelected) {
            setEditForm(prev => ({ ...prev, permissions: prev.permissions.filter(p => !permissions.includes(p)) }));
        } else {
            setEditForm(prev => ({ ...prev, permissions: [...new Set([...prev.permissions, ...permissions])] }));
        }
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        if (editForm.permissions.length === 0) {
            return setMessage({ type: 'error', text: 'Pilih minimal 1 permission' });
        }
        try {
            setEditLoading(true);
            const ipList = editForm.ip_whitelist
                ? editForm.ip_whitelist.split(',').map(ip => ip.trim()).filter(ip => ip)
                : [];
            await companyAPI.updateApiKey(companyId, editingKey.id, {
                name: editForm.name,
                permissions: editForm.permissions,
                ip_whitelist: ipList,
                rate_limit: parseInt(editForm.rate_limit, 10),
            });
            setMessage({ type: 'success', text: 'API key berhasil diperbarui' });
            setShowEditModal(false);
            setEditingKey(null);
            await fetchApiKeys();
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.detail || 'Gagal memperbarui API key' });
        } finally {
            setEditLoading(false);
        }
    };

    const selectAllInEntity = (entity, permissions) => {
        const allSelected = permissions.every(p => formData.permissions.includes(p));
        
        if (allSelected) {
            // Deselect all
            setFormData(prev => ({
                ...prev,
                permissions: prev.permissions.filter(p => !permissions.includes(p))
            }));
        } else {
            // Select all
            setFormData(prev => ({
                ...prev,
                permissions: [...new Set([...prev.permissions, ...permissions])]
            }));
        }
    };

    const handleDownloadGuide = () => {
        // Create guide content
        const guideContent = `
API Documentation - Third-Party Integration
===========================================

Last Updated: ${new Date().toLocaleDateString()}
Company: ${user?.company_name || 'Your Company'}

==================================================
TABLE OF CONTENTS
==================================================

1. Authentication
2. Rate Limiting & IP Whitelist
3. Available Endpoints
   3.1 Workspaces
   3.2 Spaces
   3.3 Tasks
   3.4 Lists
   3.5 Members
   3.6 Meetings
   3.7 Projects
   3.8 Goals
   3.9 Comments
4. Error Codes
5. Examples

==================================================
1. AUTHENTICATION
==================================================

All API requests must include your API key in the header.

Method 1: X-API-Key Header
----------------------------
curl -X GET "https://api.example.com/api/third-party/workspaces?limit=20" \\
  -H "X-API-Key: your_api_key_here"

Method 2: Authorization Bearer
-------------------------------
curl -X GET "https://api.example.com/api/third-party/workspaces?limit=20" \\
  -H "Authorization: Bearer your_api_key_here"

API Key Format:
---------------
sk_live_[32-character-random-string]

Security Notes:
---------------
- Never commit your API key to version control
- Store it securely in environment variables
- Rotate your keys regularly
- Delete unused keys immediately

==================================================
2. RATE LIMITING & IP WHITELIST
==================================================

Rate Limiting:
--------------
- Configurable per API key (default: 100 requests/minute)
- Rate limit resets every minute
- Status code 429 returned when limit exceeded

Response Headers:
-----------------
X-RateLimit-Limit: 100
X-RateLimit-Remaining: 95
X-RateLimit-Reset: 1234567890

IP Whitelist:
-------------
- Optional: restrict API key usage to specific IP addresses
- Empty whitelist = allow all IPs
- Configure via API Keys management page

==================================================
3. AVAILABLE ENDPOINTS
==================================================

Base URL: https://api.example.com/api/third-party

3.1 WORKSPACES
==============

List Workspaces
---------------
GET /workspaces?limit=20

Required Permission: read:workspaces

Response:
{
  "workspaces": [
    {
      "id": "ws_123",
      "name": "Project Alpha",
      "description": "Main project workspace",
      "company_id": "comp_456",
      "created_at": "2026-01-01T00:00:00Z"
    }
  ]
}

Get Workspace
-------------
GET /workspaces/{workspace_id}

Required Permission: read:workspaces

Response:
{
  "id": "ws_123",
  "name": "Project Alpha",
  "description": "Main project workspace",
  "members": [...],
  "created_at": "2026-01-01T00:00:00Z"
}

Create Workspace
----------------
POST /workspaces

Required Permission: write:workspaces

Request Body:
{
  "name": "New Workspace",
  "description": "Description here"
}

Response:
{
  "id": "ws_789",
  "name": "New Workspace",
  "company_id": "comp_456",
  "created_at": "2026-04-07T00:00:00Z"
}

3.2 SPACES
==========

List Spaces
-----------
GET /spaces?workspace_id=ws_123

Required Permission: read:spaces

Response:
{
  "spaces": [
    {
      "id": "space_123",
      "name": "Development",
      "workspace_id": "ws_123",
      "created_at": "2026-01-01T00:00:00Z"
    }
  ]
}

Get Space
---------
GET /spaces/{space_id}

Required Permission: read:spaces

Create Space
------------
POST /spaces

Required Permission: write:spaces

Request Body:
{
  "workspace_id": "ws_123",
  "name": "New Space",
  "description": "Space description"
}

3.3 TASKS
=========

List Tasks
----------
GET /tasks?space_id=space_123&status=active&limit=50&skip=0

Required Permission: read:tasks

Query Parameters:
- space_id (optional): Filter by space
- workspace_id (optional): Filter by workspace
- status (optional): Filter by status
- limit (optional, max 200): Number of results
- skip (optional): Pagination offset

Response:
{
  "tasks": [
    {
      "id": "task_123",
      "title": "Implement API",
      "description": "Task description",
      "status": "In Progress",
      "priority": "high",
      "assignee_ids": ["user_1", "user_2"],
      "due_date": "2026-04-15T00:00:00Z",
      "created_at": "2026-04-01T00:00:00Z"
    }
  ],
  "total": 45
}

Get Task
--------
GET /tasks/{task_id}

Required Permission: read:tasks

Create Task
-----------
POST /tasks

Required Permission: write:tasks

Request Body:
{
  "space_id": "space_123",
  "title": "New Task",
  "description": "Task description",
  "status": "To Do",
  "priority": "medium",
  "due_date": "2026-04-20T00:00:00Z",
  "assignee_ids": ["user_1"]
}

Update Task
-----------
PUT /tasks/{task_id}

Required Permission: write:tasks

Request Body:
{
  "title": "Updated Title",
  "status": "Done",
  "priority": "high"
}

Delete Task
-----------
DELETE /tasks/{task_id}

Required Permission: delete:tasks

Response:
{
  "message": "Task deleted successfully"
}

3.4 LISTS
=========

List Lists
----------
GET /lists?space_id=space_123

Required Permission: read:lists

Response:
{
  "lists": [
    {
      "id": "list_123",
      "name": "Backlog",
      "space_id": "space_123",
      "statuses": [
        {"name": "To Do", "group": "Not Started"},
        {"name": "In Progress", "group": "Active"}
      ]
    }
  ]
}

3.5 MEMBERS
===========

List Members
------------
GET /members?workspace_id=ws_123

Required Permission: read:members

Response:
{
  "members": [
    {
      "user_id": "user_123",
      "user_name": "John Doe",
      "email": "john@example.com",
      "role": "admin"
    }
  ]
}

3.6 MEETINGS
============

List Meetings
-------------
GET /meetings?workspace_id=ws_123

Required Permission: read:meetings

Response:
{
  "meetings": [
    {
      "id": "meeting_123",
      "title": "Sprint Planning",
      "start_time": "2026-04-08T10:00:00Z",
      "end_time": "2026-04-08T11:00:00Z",
      "meeting_url": "https://zoom.us/j/123456",
      "participant_ids": ["user_1", "user_2"]
    }
  ]
}

3.7 PROJECTS
============

List Projects
-------------
GET /projects?workspace_id=ws_123

Required Permission: read:projects

Response:
{
  "projects": [
    {
      "id": "proj_123",
      "name": "Q2 Initiative",
      "description": "Project description",
      "status": "active"
    }
  ]
}

3.8 GOALS
=========

List Goals
----------
GET /goals?workspace_id=ws_123

Required Permission: read:goals

Response:
{
  "goals": [
    {
      "id": "goal_123",
      "name": "Increase Performance",
      "target": 95,
      "current": 80
    }
  ]
}

3.9 COMMENTS
============

List Comments
-------------
GET /comments?task_id=task_123

Required Permission: read:comments

Response:
{
  "comments": [
    {
      "id": "comment_123",
      "text": "This looks good!",
      "created_by": "user_123",
      "created_at": "2026-04-07T10:00:00Z"
    }
  ]
}

==================================================
4. ERROR CODES
==================================================

400 Bad Request
---------------
- Invalid request parameters
- Missing required fields

Example:
{
  "detail": "workspace_id or space_id required"
}

401 Unauthorized
----------------
- Missing API key
- Invalid API key

Example:
{
  "detail": "API key required. Provide via X-API-Key or Authorization header"
}

403 Forbidden
-------------
- Insufficient permissions
- IP not in whitelist

Example:
{
  "detail": "Permission denied. Required: write:tasks"
}

404 Not Found
-------------
- Resource not found

Example:
{
  "detail": "Task not found"
}

429 Too Many Requests
---------------------
- Rate limit exceeded

Example:
{
  "detail": "Rate limit exceeded. Max 100 requests per minute"
}

500 Internal Server Error
-------------------------
- Server error

Example:
{
  "detail": "Internal server error"
}

==================================================
5. EXAMPLES
==================================================

Example 1: List all tasks in a workspace
-----------------------------------------

curl -X GET "https://api.example.com/api/third-party/tasks?workspace_id=ws_123&limit=100" \\
  -H "X-API-Key: sk_live_your_key_here"

Example 2: Create a new task
-----------------------------

curl -X POST "https://api.example.com/api/third-party/tasks" \\
  -H "X-API-Key: sk_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "space_id": "space_123",
    "title": "Fix bug in login flow",
    "description": "Users cannot login with email",
    "status": "To Do",
    "priority": "high",
    "assignee_ids": ["user_456"]
  }'

Example 3: Update task status
------------------------------

curl -X PUT "https://api.example.com/api/third-party/tasks/task_123" \\
  -H "X-API-Key: sk_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "status": "Done"
  }'

Example 4: Delete a task
-------------------------

curl -X DELETE "https://api.example.com/api/third-party/tasks/task_123" \\
  -H "X-API-Key: sk_live_your_key_here"

Example 5: Python SDK Usage
----------------------------

import requests

API_KEY = "sk_live_your_key_here"
BASE_URL = "https://api.example.com/api/third-party"

headers = {
    "X-API-Key": API_KEY,
    "Content-Type": "application/json"
}

# List tasks
response = requests.get(
    f"{BASE_URL}/tasks",
    headers=headers,
    params={"workspace_id": "ws_123", "limit": 50}
)

tasks = response.json()["tasks"]
print(f"Found {len(tasks)} tasks")

# Create task
new_task = {
    "space_id": "space_123",
    "title": "New task from API",
    "status": "To Do"
}

response = requests.post(
    f"{BASE_URL}/tasks",
    headers=headers,
    json=new_task
)

created_task = response.json()
print(f"Created task: {created_task['id']}")

Example 6: JavaScript/Node.js Usage
------------------------------------

const axios = require('axios');

const API_KEY = 'sk_live_your_key_here';
const BASE_URL = 'https://api.example.com/api/third-party';

const headers = {
  'X-API-Key': API_KEY,
  'Content-Type': 'application/json'
};

// List tasks
async function getTasks() {
  const response = await axios.get(\`\${BASE_URL}/tasks\`, {
    headers,
    params: {
      workspace_id: 'ws_123',
      limit: 50
    }
  });
  
  return response.data.tasks;
}

// Create task
async function createTask() {
  const response = await axios.post(\`\${BASE_URL}/tasks\`, {
    space_id: 'space_123',
    title: 'New task from API',
    status: 'To Do'
  }, { headers });
  
  return response.data;
}

==================================================
SUPPORT & RESOURCES
==================================================

For additional support:
- Contact: support@yourcompany.com
- Documentation: https://docs.yourcompany.com
- Status Page: https://status.yourcompany.com

API Version: 1.0
Last Updated: ${new Date().toLocaleDateString()}

==================================================
END OF DOCUMENTATION
==================================================
        `.trim();

        // Create blob and download
        const blob = new Blob([guideContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `API_Documentation_${new Date().getTime()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        setMessage({
            type: 'success',
            text: 'API Documentation downloaded successfully!'
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    };

    if (!companyId) {
        // When embedded, render the alert inline without the Layout wrapper
        const noCompanyAlert = (
            <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                    API Key management is only available for companies.
                </AlertDescription>
            </Alert>
        );
        if (embedded) {
            return <div className="p-6">{noCompanyAlert}</div>;
        }
        return (
            <Layout>
                <div className="max-w-6xl mx-auto p-6">
                    {noCompanyAlert}
                </div>
            </Layout>
        );
    }

    const content = (
        <div className={embedded ? "space-y-6" : "max-w-6xl mx-auto p-6 space-y-6"}>
            {/* Header */}
            <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <Key className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-brand-text-primary">API Keys</h1>
                            <p className="text-sm text-brand-text-muted">
                                Manage third-party API access to your company data
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={handleDownloadGuide}>
                            <Download className="w-4 h-4 mr-2" />
                            Download API Docs
                        </Button>
                        <Button onClick={() => setShowCreateModal(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Create API Key
                        </Button>
                    </div>
                </div>

                {/* Message */}
                {message.text && (
                    <Alert className={message.type === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
                        {message.type === 'success' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                            <AlertCircle className="w-4 h-4 text-red-600" />
                        )}
                        <AlertDescription className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                            {message.text}
                        </AlertDescription>
                    </Alert>
                )}

                {/* API Keys List */}
                <Card>
                    <CardHeader>
                        <CardTitle>Active API Keys</CardTitle>
                        <CardDescription>
                            API keys allow external applications to access your data
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {apiKeys.length === 0 ? (
                            <div className="text-center py-12">
                                <Key className="w-12 h-12 mx-auto mb-4 text-brand-text-muted opacity-50" />
                                <p className="text-brand-text-muted mb-4">No API keys yet</p>
                                <Button variant="outline" onClick={() => setShowCreateModal(true)}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create your first API key
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {apiKeys.map(key => (
                                    <div
                                        key={key.id}
                                        className="border border-brand-border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="font-semibold text-brand-text-primary">{key.name}</h3>
                                                    <Badge variant="outline" className="text-xs">
                                                        {key.permissions.length} permissions
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono" data-testid={`api-key-preview-${key.id}`}>
                                                        {key.key_preview}
                                                    </code>
                                                    <span className="text-xs text-brand-text-muted italic">
                                                        Identifier saja — full key hanya tampil saat dibuat
                                                    </span>
                                                </div>
                                                <div className="text-xs text-brand-text-muted space-y-1">
                                                    <p><strong>Rate Limit:</strong> {key.rate_limit} requests/min</p>
                                                    <p><strong>Permissions:</strong> {(key.permissions || []).join(', ') || '—'}</p>
                                                    {key.ip_whitelist && key.ip_whitelist.length > 0 && (
                                                        <p><strong>IP Whitelist:</strong> {key.ip_whitelist.join(', ')}</p>
                                                    )}
                                                    <p>Created: {new Date(key.created_at).toLocaleDateString()}</p>
                                                    {key.last_used_at && (
                                                        <p>Last used: {new Date(key.last_used_at).toLocaleDateString()}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1 ml-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEditModal(key)}
                                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                    data-testid={`api-key-edit-${key.id}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDeleteApiKey(key.id)}
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    data-testid={`api-key-delete-${key.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Info Card */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Security Best Practices</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm text-brand-text-muted">
                        <p>• Store API keys securely and never commit them to version control</p>
                        <p>• Use IP whitelisting to restrict access from specific IP addresses</p>
                        <p>• Grant only the minimum permissions required for your use case</p>
                        <p>• Rotate API keys regularly</p>
                        <p>• Delete unused API keys immediately</p>
                        <p>• Monitor API key usage via "Last used" timestamp</p>
                    </CardContent>
                </Card>

                {/* Create API Key Modal */}
                <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Create API Key</DialogTitle>
                            <DialogDescription>
                                Configure permissions and settings for your new API key
                            </DialogDescription>
                        </DialogHeader>
                        
                        <form onSubmit={handleCreateApiKey} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Key Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g., Production Integration"
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Permissions *</Label>
                                <div className="border border-brand-border rounded-lg p-4 max-h-64 overflow-y-auto">
                                    {Object.entries(availablePermissions).map(([entity, permissions]) => {
                                        const allSelected = permissions.every(p => formData.permissions.includes(p));
                                        const someSelected = permissions.some(p => formData.permissions.includes(p));
                                        
                                        return (
                                            <Collapsible
                                                key={entity}
                                                open={expandedSections[entity]}
                                                onOpenChange={() => toggleSection(entity)}
                                                className="mb-2"
                                            >
                                                <div className="flex items-center gap-2 py-2 border-b">
                                                    <CollapsibleTrigger className="flex items-center gap-2 flex-1">
                                                        {expandedSections[entity] ? (
                                                            <ChevronDown className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4" />
                                                        )}
                                                        <span className="font-medium capitalize">{entity}</span>
                                                        {someSelected && (
                                                            <Badge variant="secondary" className="ml-2 text-xs">
                                                                {permissions.filter(p => formData.permissions.includes(p)).length}/{permissions.length}
                                                            </Badge>
                                                        )}
                                                    </CollapsibleTrigger>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => selectAllInEntity(entity, permissions)}
                                                        className="text-xs"
                                                    >
                                                        {allSelected ? 'Deselect All' : 'Select All'}
                                                    </Button>
                                                </div>
                                                <CollapsibleContent className="pl-6 pt-2 space-y-2">
                                                    {permissions.map(permission => (
                                                        <div key={permission} className="flex items-center gap-2">
                                                            <Checkbox
                                                                id={permission}
                                                                checked={formData.permissions.includes(permission)}
                                                                onCheckedChange={() => togglePermission(permission)}
                                                            />
                                                            <Label
                                                                htmlFor={permission}
                                                                className="text-sm cursor-pointer"
                                                            >
                                                                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                                                                    {permission}
                                                                </code>
                                                            </Label>
                                                        </div>
                                                    ))}
                                                </CollapsibleContent>
                                            </Collapsible>
                                        );
                                    })}
                                </div>
                                {formData.permissions.length === 0 && (
                                    <p className="text-xs text-red-600">Please select at least one permission</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="rate_limit">Rate Limit (requests per minute)</Label>
                                <Input
                                    id="rate_limit"
                                    type="number"
                                    value={formData.rate_limit}
                                    onChange={(e) => setFormData(prev => ({ ...prev, rate_limit: e.target.value }))}
                                    min="1"
                                    max="1000"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="ip_whitelist">IP Whitelist (optional)</Label>
                                <Input
                                    id="ip_whitelist"
                                    value={formData.ip_whitelist}
                                    onChange={(e) => setFormData(prev => ({ ...prev, ip_whitelist: e.target.value }))}
                                    placeholder="e.g., 192.168.1.1, 10.0.0.1"
                                />
                                <p className="text-xs text-brand-text-muted">
                                    Comma-separated list of allowed IP addresses. Leave empty to allow all IPs.
                                </p>
                            </div>

                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowCreateModal(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={loading || formData.permissions.length === 0}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Key className="w-4 h-4 mr-2" />
                                            Create API Key
                                        </>
                                    )}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Edit API Key Modal */}
                <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="api-key-edit-modal">
                        <DialogHeader>
                            <DialogTitle>Edit API Key</DialogTitle>
                            <DialogDescription>
                                Ubah nama, permission, IP whitelist, atau rate limit. Key value tidak berubah.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleEditSave} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="edit-name">Key Name</Label>
                                <Input
                                    id="edit-name"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                    required
                                    data-testid="api-key-edit-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Permissions *</Label>
                                <div className="border border-brand-border rounded-lg p-4 max-h-64 overflow-y-auto">
                                    {Object.entries(availablePermissions).map(([entity, permissions]) => {
                                        const allSelected = permissions.every(p => editForm.permissions.includes(p));
                                        const someSelected = permissions.some(p => editForm.permissions.includes(p));
                                        const open = !!editExpanded[entity];
                                        return (
                                            <Collapsible key={entity} open={open} onOpenChange={() => setEditExpanded(prev => ({ ...prev, [entity]: !prev[entity] }))} className="mb-2">
                                                <div className="flex items-center gap-2 py-2 border-b">
                                                    <CollapsibleTrigger className="flex items-center gap-2 flex-1">
                                                        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                        <span className="font-medium capitalize">{entity}</span>
                                                        {someSelected && (
                                                            <Badge variant="secondary" className="ml-2 text-xs">
                                                                {permissions.filter(p => editForm.permissions.includes(p)).length}/{permissions.length}
                                                            </Badge>
                                                        )}
                                                    </CollapsibleTrigger>
                                                    <Button type="button" variant="ghost" size="sm" onClick={() => selectAllInEntityEdit(entity, permissions)} className="text-xs">
                                                        {allSelected ? 'Deselect All' : 'Select All'}
                                                    </Button>
                                                </div>
                                                <CollapsibleContent className="pl-6 pt-2 space-y-2">
                                                    {permissions.map(permission => (
                                                        <div key={permission} className="flex items-center gap-2">
                                                            <Checkbox
                                                                id={`edit-${permission}`}
                                                                checked={editForm.permissions.includes(permission)}
                                                                onCheckedChange={() => toggleEditPermission(permission)}
                                                                data-testid={`api-key-edit-perm-${permission}`}
                                                            />
                                                            <Label htmlFor={`edit-${permission}`} className="text-sm cursor-pointer">
                                                                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{permission}</code>
                                                            </Label>
                                                        </div>
                                                    ))}
                                                </CollapsibleContent>
                                            </Collapsible>
                                        );
                                    })}
                                </div>
                                {editForm.permissions.length === 0 && (
                                    <p className="text-xs text-red-600">Pilih minimal 1 permission</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-rate">Rate Limit (requests per minute)</Label>
                                <Input
                                    id="edit-rate"
                                    type="number"
                                    value={editForm.rate_limit}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, rate_limit: e.target.value }))}
                                    min="1"
                                    max="1000"
                                    data-testid="api-key-edit-rate-limit"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-ip">IP Whitelist (optional)</Label>
                                <Input
                                    id="edit-ip"
                                    value={editForm.ip_whitelist}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, ip_whitelist: e.target.value }))}
                                    placeholder="e.g., 192.168.1.1, 10.0.0.1"
                                    data-testid="api-key-edit-ip"
                                />
                                <p className="text-xs text-brand-text-muted">Comma-separated. Kosong = allow all.</p>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
                                <Button type="submit" disabled={editLoading || editForm.permissions.length === 0} data-testid="api-key-edit-save">
                                    {editLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Pencil className="w-4 h-4 mr-2" />Save Changes</>}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Show New API Key Modal */}
                <Dialog open={showKeyModal} onOpenChange={setShowKeyModal}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                API Key Created Successfully
                            </DialogTitle>
                            <DialogDescription>
                                Make sure to copy your API key now. You won't be able to see it again!
                            </DialogDescription>
                        </DialogHeader>
                        
                        {newApiKey && (
                            <div className="space-y-4">
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <div className="flex items-start gap-2">
                                        <Shield className="w-5 h-5 text-yellow-600 mt-0.5" />
                                        <div className="flex-1 text-sm text-yellow-800">
                                            <p className="font-semibold mb-1">Important Security Notice</p>
                                            <p>This is the only time you will see this key. Store it securely.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Your API Key</Label>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-sm bg-gray-100 px-3 py-2 rounded font-mono break-all">
                                            {newApiKey.key}
                                        </code>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => copyToClipboard(newApiKey.key)}
                                        >
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="text-sm space-y-1 text-brand-text-muted">
                                    <p><strong>Name:</strong> {newApiKey.name}</p>
                                    <p><strong>Permissions:</strong> {newApiKey.permissions.join(', ')}</p>
                                    <p><strong>Rate Limit:</strong> {newApiKey.rate_limit} requests/min</p>
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button onClick={() => setShowKeyModal(false)}>
                                Done
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
        </div>
    );

    if (embedded) {
        return content;
    }

    return (
        <Layout>
            {content}
        </Layout>
    );
}

/**
 * Full page wrapper for backward compatibility with the /api-keys route.
 * Renders <APIKeyManagementContent /> inside the standard <Layout>.
 */
export default function APIKeyManagementPage() {
    return <APIKeyManagementContent />;
}
